import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { Select, Textarea, SuccessScreen } from 'shared/components';
import * as loginActions from 'core/duck';
import * as staticActions from 'shared/ducks/staticDuck';
import * as sendEmailActions from 'features/profile/send-email-container/duck';
import {
  mapTcOptions,
  mapEmailSubjects,
  getMappedTestCenters,
} from 'features/profile/send-email-container/selectors';
import { emailValidationRules, emailErrorStrings } from 'shared/validations';
import { ValidationService } from 'shared/services';
import isEmpty from 'lodash/isEmpty';
import MaterialIcon from 'material-icons-react';
import emailSent from 'assets/svgs/emailSent.svg';
import './SendEmailContainer.scss';

const MAX_LENGTH = 1500;

class SendEmailContainer extends Component {
  constructor(props) {
    super(props);

    this.state = {
      emailContent: {
        subject: props.location.state ? props.location.state.preselectedSubject : '',
        description: '',
        testCenterId: null,
        testCenterEventId: null,
      },
      tcEventOptions: [],
      emailSentSuccess: false,
      errors: {},
    };

    this.onChange = this.onChange.bind(this);
    this.onTestCenterChange = this.onTestCenterChange.bind(this);
    this.onSubmitEmail = this.onSubmitEmail.bind(this);
    this.renderEmailForm = this.renderEmailForm.bind(this);
    this.renderSuccessScreen = this.renderSuccessScreen.bind(this);
    this.setEventOptions = this.setEventOptions.bind(this);

    if (!this.props.emailSubjects.length) {
      this.props.actions.getEmailSubjects();
    }
    this.props.actions.getUserEvents(props.user.id);
  }

  setEventOptions(val) {
    const { mappedTestCenters } = this.props;

    const tcEventOptions = [
      {
        value: -1,
        label: this.props.t('profile.email.notApplicableLabel'),
      },
    ];

    if (mappedTestCenters[val]) {
      mappedTestCenters[val].events.forEach((ev) => {
        tcEventOptions.push({
          value: ev.id,
          label: ev.testingEvent.name,
        });
      });
    }

    this.setState({
      tcEventOptions,
    });
  }

  onTestCenterChange(e) {
    const { value, name } = e.target;
    const { mappedTestCenters } = this.props;

    this.setState((prevState) => ({
      emailContent: {
        ...prevState.emailContent,
        [name]: mappedTestCenters[+value] ? mappedTestCenters[+value].testCenterId : -1,
      },
      errors: {
        ...prevState.errors,
        [name]: '',
      },
    }));

    this.setEventOptions(+value);
  }

  onChange(e) {
    const { value, name } = e.target;

    this.setState((prevState) => ({
      emailContent: {
        ...prevState.emailContent,
        [name]: !+value ? value : +value,
      },
      errors: {
        ...prevState.errors,
        [name]: '',
      },
    }));
  }

  onSubmitEmail() {
    const { emailContent } = this.state;
    const errors = ValidationService.validate(
      emailContent,
      emailValidationRules,
      emailErrorStrings
    );

    if (!isEmpty(errors)) {
      this.setState({
        errors,
      });
      return;
    }
    const content = {
      ...emailContent,
      testCenterEventId: emailContent.testCenterEventId > 0 ? emailContent.testCenterEventId : null,
      testCenterId: emailContent.testCenterId > 0 ? emailContent.testCenterId : null,
    };

    this.props.actions.sendEmail(content).then(() => {
      if (!this.props.loadingEmail && !this.props.errorSendEmail) {
        this.setState({
          emailSentSuccess: true,
        });
      }
    });
  }

  renderEmailForm() {
    const { user, errorSendEmail, tcOptions, subjectOptions } = this.props;

    const { emailContent, errors, tcEventOptions } = this.state;

    return (
      <div className="col  mx-auto send-email-container">
        <h4>{this.props.t('profile.email.title')}</h4>
        <p
          className="dark-gray"
          dangerouslySetInnerHTML={{ __html: this.props.t('profile.email.infoMessage') }}
        />
        <p className="mb-0">
          {this.props.t('profile.email.message')}
          {}
        </p>
        <p className="fw-bold pb-3">{user.emailAddress}</p>
        {errorSendEmail && (
          <div className="row m-0 mb-4 error-section">
            <p className="col-1 mb-0">
              <MaterialIcon icon="warning" size="small" />
            </p>
            <p className="col-11 mb-0">{this.props.t('profile.email.errorMessage')}</p>
          </div>
        )}
        <div className="select-container">
          <Select
            className=""
            options={subjectOptions}
            value={emailContent.subject}
            label={this.props.t('forms.placeholders.selectEmailSubject')}
            name="subject"
            onChange={this.onChange}
            errors={errors.subject}
            emptyDefault
          />
          <Select
            className=""
            options={tcOptions}
            label={this.props.t('forms.placeholders.selectTestCenter')}
            name="testCenterId"
            onChange={this.onTestCenterChange}
            errors={errors.testCenterId}
            emptyDefault
          />
          <Select
            className=""
            options={tcEventOptions}
            label={this.props.t('forms.placeholders.selectTestCenterEvent')}
            name="testCenterEventId"
            onChange={this.onChange}
            disabled={!emailContent.testCenterId}
            errors={errors.testCenterEventId}
            emptyDefault
          />
        </div>
        <div>
          <Textarea
            className="mb-1"
            name="description"
            value={emailContent.description}
            label={this.props.t('forms.placeholders.writeEmail')}
            onChange={this.onChange}
            maxLength={MAX_LENGTH}
            optionalText="profile.email.textareaDescription"
            error={errors.description}
          />
        </div>
        <div className="buttons-container w-100">
          <button
            type="button"
            className="btn btn-outline w-50"
            onClick={() => this.props.history.goBack()}>
            {this.props.t('common.labels.back')}
          </button>
          <button type="button" className="btn btn-primary w-50" onClick={this.onSubmitEmail}>
            {this.props.t('profile.email.sendEmailLabel')}
          </button>
        </div>
      </div>
    );
  }

  renderSuccessScreen() {
    const { user } = this.props;
    const successData = {
      svg: emailSent,
      copy: 'profile.contactSupport.emailSentSuccessfullyTitle',
      message: 'profile.contactSupport.emailSentSuccessfullyMessage',
      messageDynamic: {
        email: user.emailAddress,
      },
      buttonClass: 'btn-primary',
      buttonLabel: 'common.labels.continueToDashboard',
      link: '/dashboard',
    };

    return <SuccessScreen data={successData} />;
  }

  render() {
    const { emailSentSuccess } = this.state;

    return !emailSentSuccess ? this.renderEmailForm() : this.renderSuccessScreen();
  }
}

function mapStateToProps(state) {
  return {
    testCenters: state.email.testCenters,
    user: state.users.currentUser,
    emailSubjects: state.statics.emailSubjects,
    errorSendEmail: state.email.errorSendEmail,
    loadingEmail: state.email.loadingEmail,
    tcOptions: mapTcOptions(state),
    subjectOptions: mapEmailSubjects(state),
    userEvents: state.email.userEvents,
    mappedTestCenters: getMappedTestCenters(state),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...sendEmailActions,
        ...loginActions,
        ...staticActions,
      },
      dispatch
    ),
  };
}

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(SendEmailContainer));

SendEmailContainer.propTypes = {
  emailSubjects: PropTypes.arrayOf(PropTypes.string).isRequired,
  mappedTestCenters: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  user: PropTypes.shape({
    id: PropTypes.number,
  }).isRequired,
  tcOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  actions: PropTypes.shape({
    getEmailSubjects: PropTypes.func.isRequired,
    sendEmail: PropTypes.func.isRequired,
  }).isRequired,
  loadingEmail: PropTypes.bool.isRequired,
  errorSendEmail: PropTypes.string.isRequired,
  history: PropTypes.shape({
    goBack: PropTypes.func.isRequired,
  }).isRequired,
  t: PropTypes.func.isRequired,
};
